import { useState, useEffect } from 'react';

/**
 * Hook to track state of a CSS media query
 * 
 * @param query - Media query string like '(min-width: 768px)'
 * @returns boolean - Whether the media query matches
 */
export function useMediaQuery(query: string): boolean {
  // Assegna un valore predefinito per SSR (no window)
  const getMatches = (): boolean => {
    // Gestisce SSR
    if (typeof window === 'undefined') {
      return false;
    }
    
    return window.matchMedia(query).matches;
  };
  
  // State per tracciare se il media query corrisponde
  const [matches, setMatches] = useState<boolean>(getMatches());
  
  // Gestione dei cambiamenti nel media query
  useEffect(() => {
    // Gestisce SSR
    if (typeof window === 'undefined') {
      return;
    }
    
    // Funzione per ottenere il valore attuale
    const updateMatches = () => {
      setMatches(getMatches());
    };
    
    // Ascolta i cambiamenti nella finestra
    window.addEventListener('resize', updateMatches);
    
    // Supporto per MediaQueryList.addEventListener (metodo più nuovo)
    const mediaQueryList = window.matchMedia(query);
    
    // Usa il metodo appropriato a seconda della disponibilità del browser
    try {
      // Versione moderna (Chrome, Firefox, Safari 14+)
      mediaQueryList.addEventListener('change', updateMatches);
      
      // Pulizia
      return () => {
        mediaQueryList.removeEventListener('change', updateMatches);
        window.removeEventListener('resize', updateMatches);
      };
    } catch (e) {
      // Versione precedente (Safari <= 13, IE)
      try {
        // Metodo legacy (Safari <= 13, IE)
        mediaQueryList.addListener(updateMatches);
        
        // Pulizia
        return () => {
          mediaQueryList.removeListener(updateMatches);
          window.removeEventListener('resize', updateMatches);
        };
      } catch (e2) {
        // Fallback per browser che non supportano matchMedia listener
        // Usa solo gli eventi resize della finestra
        return () => {
          window.removeEventListener('resize', updateMatches);
        };
      }
    }
  }, [query]);
  
  return matches;
}